import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@wconcept/wconcept-ui-renewal/components/atoms/Box';
import MainBannerItemType2 from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MainBannerItemType2';
import MainBannerSwiperType2 from '@wconcept/wconcept-ui-renewal/components/organisms/_list/MainBannerSwiperType2';
import MegaPromotionSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/MegaPromotionSkeleton';
import { PromotionMainBannerModule } from '@wconcept/wconcept-common/models/renewal/promotion';
import useLinkTo from '@hooks/useLinkTo';
import useGa4Event from '@hooks/useGa4Event';

interface MainBannerModuleProps {
  modules: PromotionMainBannerModule[];
  isMobile: boolean;
}

function shuffleModules(
  list: PromotionMainBannerModule[]
): PromotionMainBannerModule[] {
  const next = [...list];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function MainBannerModule({ modules, isMobile }: MainBannerModuleProps) {
  const { linkTo } = useLinkTo();
  const { sendGa4BannerClick } = useGa4Event();

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [orderedModules] = useState(() => {
    const fixedSorted = modules
      .filter(m => m.displayOrderMode !== 'RANDOM')
      .sort(
        (a, b) =>
          (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.displayOrder ?? Number.MAX_SAFE_INTEGER)
      );
    const randomShuffled = shuffleModules(
      modules.filter(m => m.displayOrderMode === 'RANDOM')
    );
    return [...fixedSorted, ...randomShuffled];
  });

  const onClickBanner = useCallback(
    (module: PromotionMainBannerModule, listNo: number) => {
      sendGa4BannerClick({
        page: 'promotion',
        area: 'promotion_main_banner',
        text: module.title || undefined,
        listNo,
        bannerType: module.ga4ClickEventResponse?.eventType,
        issueCd: module.ga4ClickEventResponse?.eventValue,
      });
      if (module.landingUrl) {
        linkTo.push({
          url: module.landingUrl,
        });
      }
    },
    [linkTo, sendGa4BannerClick]
  );

  const items = useMemo(
    () =>
      orderedModules.map((module, index) => (
        <MainBannerItemType2
          key={module.moduleId}
          title1={module.mainText1}
          title2={module.mainText2}
          subTitle={module.subText}
          imgSrc={module.imageUrl}
          rs={isMobile ? '860' : '750'}
          priority={index < 3}
          onClick={() => onClickBanner(module, index + 1)}
        />
      )),
    [orderedModules, isMobile, onClickBanner]
  );

  if (!isMounted) {
    return (
      <Box as="section" className="corner-mega-main-banner">
        <MegaPromotionSkeleton />
      </Box>
    );
  }

  return (
    <Box as="section" className="corner-mega-main-banner">
      <MainBannerSwiperType2
        items={items}
        isDesktop={!isMobile}
        autoPlayEnabled={orderedModules.length > 1}
      />
    </Box>
  );
}

export default memo(MainBannerModule);
